import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  TextField,
  FormControl,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import { Box } from '@mui/system';
import Autocomplete from '@mui/material/Autocomplete';
import DialogActions from '@mui/material/DialogActions';
import { useServerRequest } from 'src/hooks/useServerRequest';
import { getBanks } from 'src/utils/accounts/banks';
import Iconify from 'src/components/iconify/iconify';
import { useSnackbar } from 'src/components/snackbar';
import { useDispatch } from 'react-redux';
import { addForeignBeneficiary, addLocalBeneficiary } from 'src/redux/slices/transfersSlice';
import { useLocales } from 'src/locales';

interface RibDialogProps {
  open: boolean;
  onClose: () => void;
  typ?: '1' | '2';
}

interface Bank {
  id: number;
  name: string;
}
const roles = [
  { id: '1', label: 'Local Beneficiary' },
  { id: '2', label: 'Foreign Beneficiary' },
];
const AddLocal: React.FC<RibDialogProps> = ({ open, onClose, typ }) => {
  const banks: Bank[] = getBanks();
  const [selectedBank, setSelectedBank] = useState<Bank | null>(null);
  const [rib, setRib] = useState<string>('');
  const [iban, setIban] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [attestation, setAttestation] = useState<File | null>(null);
  const [type, setType] = useState<string>(typ || roles[0].id);
  const [bic, setBic] = useState('');
  const [bankName, setBankName] = useState('');
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const { t } = useLocales();
  const { makeRequest } = useServerRequest();

  const handleRibChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value.replace(/\s+/g, '');
    if (/^\d*$/.test(input) && input.length <= 24) {
      const formattedRib = input.replace(/(.{4})/g, '$1 ').trim();
      setRib(formattedRib);
    }
  };

  const handleTypeChange = (event: React.MouseEvent<HTMLElement>, newType: string | null) => {
    if (newType !== null) {
      setType(newType);
    }
  };

  const handleAttestationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setAttestation(e.target.files[0]);
    }
  };
  const handleFileUploadClick = () => {
    document.getElementById('file-upload')?.click();
  };

  const handleSubmit = async () => {
    // Validate common fields
    if (!name) {
      enqueueSnackbar(t('common.input.benef.error2'), { variant: 'error' });
      return;
    }

    // Handle Local Beneficiary submission
    if (type === '1') {
      if (!selectedBank || !rib) {
        enqueueSnackbar(t('transfers.m4'), { variant: 'error' });
        return;
      }

      const formData = new FormData();
      formData.append('name', name);
      formData.append('bank_id', selectedBank.id.toString());
      formData.append('rib', rib.replace(/\s+/g, ''));
      if (attestation) {
        formData.append('attestation', attestation as Blob);
      }

      const response = await makeRequest(
        `/saveLocalBeneficiaries`,
        {
          method: 'POST',
          data: formData,
        },
        t('transfers.m5')
      );

      if (response.success && response.data) {
        dispatch(addLocalBeneficiary(response.data.beneficiary));
        onClose();
      }
    }
    // Handle Foreign Beneficiary submission
    else {
      if (!bankName || !bic || !iban) {
        enqueueSnackbar(t('transfers.m6'), { variant: 'error' });
        return;
      }

      const formData = new FormData();
      formData.append('name', name);
      formData.append('bankName', bankName);
      formData.append('swift', bic);
      formData.append('iban', iban.replace(/\s+/g, ''));
      if (attestation) {
        formData.append('attestation', attestation as Blob);
      }

      const response = await makeRequest(
        `/saveForeignBeneficiaries`,
        {
          method: 'POST',
          data: formData,
        },
        t('transfers.m5')
      );

      if (response.success && response.data) {
        dispatch(addForeignBeneficiary(response.data.beneficiary));
        onClose();
      }
    }
  };


  return (
    <Dialog open={open} disableEscapeKeyDown onClose={(_, reason) => reason !== 'backdropClick' && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>{t('m.t.benef')}</DialogTitle>
      <DialogContent>
        <FormControl fullWidth margin="dense" required>
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            {/* Label in one line with toggle buttons */}
            <Typography variant="body1" component="label" sx={{ mr: 2 }}>
              {t('common.input.selectType')}:
            </Typography>
            <ToggleButtonGroup
              value={type}
              exclusive
              onChange={handleTypeChange}
              aria-label={t('common.input.type.label')}
              sx={{ flexGrow: 1 }}
            >
              {roles.map((roleObj) => (
                <ToggleButton
                  key={roleObj.id}
                  value={roleObj.id}
                  aria-label={roleObj.label}
                  sx={{ flex: 1 }}
                >
                  {roleObj.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
        </FormControl>

        <TextField
          fullWidth
          margin="normal"
          label={t('transfers.benefs.name')}
          value={name}
          onChange={(e) => setName(e.target.value.toUpperCase())}
          placeholder={t('common.input.benef.placehoder')}
        />

        {type === '1' ? <FormControl fullWidth margin="normal">
          <Autocomplete
            fullWidth
            value={selectedBank}
            options={banks}
            getOptionLabel={(option) => option.name}
            onChange={(event, newValue) => setSelectedBank(newValue)}
            renderInput={(params) => <TextField {...params} label="Select Bank" />}
          />
        </FormControl> : <TextField
          fullWidth
          margin="normal"
          label={t('common.input.rib.label2')}
          value={bankName}
          onChange={(e) => { setBankName(e.target.value.toUpperCase()) }}
          placeholder={t('common.input.rib.placeholder')}
        />}

        {type === '1' ? <TextField
          fullWidth
          margin="normal"
          label={t('common.input.rib.label')}
          value={rib}
          onChange={handleRibChange}
          placeholder={t('common.input.rib.select2')}
        /> : <TextField
          fullWidth
          margin="normal"
          label={t('common.input.rib.iban')}
          value={iban}
          onChange={(e) => { setIban(e.target.value.toUpperCase()) }}
          placeholder={t('common.input.rib.select3')}
        />}

        {type !== '1' && <TextField
          fullWidth
          margin="normal"
          label={t('common.input.rib.swift')}
          value={bic}
          onChange={(e) => { setBic(e.target.value.toUpperCase()) }}
          placeholder={t('common.input.rib.swift')}
        />}
        <Button
          fullWidth
          variant={!attestation ? "outlined" : "contained"}
          onClick={handleFileUploadClick}
          sx={{ marginTop: 1, p: 2, backgroundColor: 'success' }}
          startIcon={<Iconify icon="mingcute:upload-3-fill" />}
        >
          {t('common.button.action.upload')}
        </Button>
        <input
          id="file-upload"
          type="file"
          accept=".pdf,.jpg,.png"
          style={{ display: 'none' }}
          onChange={handleAttestationChange}
        />

      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined" color="inherit">
          {t('common.button.action.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
        >
          {t('common.button.action.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddLocal;
